import React, { useState, useEffect, forwardRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    TextField,
    MenuItem,
    Chip,
    Button,
    Fade,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import { getAllCollabs, getPostes } from "../../../components/collabs/collabsSlice";
import { getTeams } from "../../../components/teams/teamsSlice";
import {
    addOpportunity,
    editOpportunity,
    getOpportunities,
} from "../../../components/clients/clientsSlice";
import moment from "moment";

/* ── slide-up transition ── */
const SlideUp = forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── transition constant ── */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

/* ── glassmorphic field wrapper ── */
const glassFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        backgroundColor: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(16px) saturate(1.4)",
        WebkitBackdropFilter: "blur(16px) saturate(1.4)",
        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 8px rgba(0,0,0,0.03)",
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.6)",
            boxShadow:
                "0 0 0 4px rgba(255,144,21,0.12), 0 0 20px rgba(255,144,21,0.06)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.85)",
            boxShadow:
                "0 4px 16px rgba(255,144,21,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
        },
        "&.Mui-disabled": {
            backgroundColor: "rgba(0,0,0,0.03)",
            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
        },
    },
    "& .MuiInputBase-input": {
        fontSize: "16px",
        color: "rgba(0,0,0,0.87)",
    },
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "rgba(0,0,0,0.45)",
    },
    "& .MuiSelect-icon": {
        color: "rgba(0,0,0,0.4)",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const selectMenuProps = {
    sx: { zIndex: 1600 },
};

/* ── option arrays ── */
const categoryOptions = [
    "Demande de Profil",
    "Besoin Profil Interne",
    "Projet Bureautique",
    "Projet Infra",
    "Infogérance",
    "Autres",
];

const priorityOptions = ["Normale", "Urgent", "Faible"];

const statusOptions = [
    "Non démarré",
    "En cours",
    "Clos Gagné",
    "Clos Abandonné",
    "Clos Perdu",
    "Non pris en charge",
];

const subStatusOptions = [
    "-",
    "Recherche de profils",
    "RDV candidats",
    "CV(s) transmis",
    "Relance",
    "Attente retour client",
    "Attente retour Responsable de compte",
];

/* ── chip color helpers ── */
function categoryChipProps(value) {
    switch (value) {
        case "Demande de Profil":
            return { color: "info", label: "Demande de Profil" };
        case "Besoin Profil Interne":
            return { color: "primary", label: "Besoin Profil Interne" };
        case "Projet Bureautique":
            return { color: "success", label: "Projet Bureautique" };
        case "Projet Infra":
            return { color: "warning", label: "Projet Infra" };
        case "Infogérance":
            return { color: "secondary", label: "Infogérance" };
        case "Autres":
            return { color: "error", label: "Autres" };
        default:
            return { color: "default", label: value || "—" };
    }
}

function statusChipProps(value) {
    switch (value) {
        case "Non démarré":
            return { color: "default", label: "Non démarré" };
        case "En cours":
            return { color: "info", label: "En cours" };
        case "Clos Gagné":
            return { color: "success", label: "Clos Gagné" };
        case "Clos Abandonné":
            return { color: "warning", label: "Clos Abandonné" };
        case "Clos Perdu":
            return { color: "error", label: "Clos Perdu" };
        case "Non pris en charge":
            return { color: "error", label: "Non pris en charge" };
        default:
            return { color: "default", label: value || "—" };
    }
}

function subStatusChipProps(value) {
    switch (value) {
        case "-":
            return { color: "default", label: "-" };
        case "Recherche de profils":
            return { color: "info", label: "Recherche de profils" };
        case "RDV candidats":
            return { color: "primary", label: "RDV candidats" };
        case "CV(s) transmis":
            return { color: "success", label: "CV(s) transmis" };
        case "Relance":
            return { color: "warning", label: "Relance" };
        case "Attente retour client":
            return { color: "warning", label: "Attente retour client" };
        case "Attente retour Responsable de compte":
            return { color: "warning", label: "Attente retour Responsable de compte" };
        default:
            return { color: "default", label: value || "—" };
    }
}

function priorityColor(value) {
    if (value === "Normale") return "success";
    if (value === "Urgent") return "error";
    if (value === "Faible") return "default";
    return "default";
}

/* ── date helper ── */
function toDateStr(val) {
    if (!val) return "";
    return moment(val).format("YYYY-MM-DD");
}

/* ════════════════════════════════════════════════════════════════════ */
export default function MobileOpportunityModal({
    visible,
    setVisible,
    type,
    onViewItem,
    setOnViewItem,
}) {
    const dispatch = useDispatch();

    const postes = useSelector((state) => state.collabs.postes);
    const teams = useSelector((state) => state.teams.allTeams);
    const allCollabs = useSelector((state) => state.collabs.allCollabsList);
    const userId = useSelector((state) => state.login.userId);

    const [selectedItem, setSelectedItem] = useState(null);
    const [originalItem, setOriginalItem] = useState(null);

    /* ── on open: fetch dropdown data + init state ── */
    useEffect(() => {
        if (visible) {
            dispatch(getPostes());
            dispatch(getTeams());
            dispatch(getAllCollabs());
            if (type === "EDIT") {
                setSelectedItem(onViewItem ? { ...onViewItem } : null);
                setOriginalItem(onViewItem ? { ...onViewItem } : null);
            } else {
                const newItem = { demanded_by: parseInt(userId) };
                setSelectedItem(newItem);
                setOriginalItem(newItem);
            }
        }
    }, [visible]);

    /* ── dirty check ── */
    const isDirty = useMemo(() => {
        if (!originalItem || !selectedItem) return false;
        return JSON.stringify(originalItem) !== JSON.stringify(selectedItem);
    }, [originalItem, selectedItem]);

    /* ── always show save for ADD, only when dirty for EDIT ── */
    const showSave = type === "ADD" || isDirty;

    /* ── field change handler ── */
    const handleFieldChange = (value, key) => {
        setSelectedItem((prev) => ({ ...prev, [key]: value }));
    };

    /* ── close handler ── */
    const handleClose = () => {
        setVisible(false);
        setSelectedItem(null);
        setOriginalItem(null);
        if (setOnViewItem) setOnViewItem(null);
    };

    /* ── save handler ── */
    const handleSave = () => {
        if (
            !selectedItem ||
            !selectedItem.title ||
            typeof selectedItem.title !== "string" ||
            selectedItem.title.trim() === ""
        ) {
            showErrorToast('Veuillez remplir les champs obligatoires "titre" .');
            return;
        }

        const opp = { ...selectedItem };
        opp.start_date = opp.start_date
            ? moment(opp.start_date).format("YYYY-MM-DD 00:00:00")
            : null;

        if (type === "EDIT") {
            dispatch(editOpportunity({ opportunity: opp })).then((actionResponse) => {
                if (editOpportunity.fulfilled.match(actionResponse)) {
                    if (
                        actionResponse.payload.responseData &&
                        actionResponse.payload.responseData.status
                    ) {
                        dispatch(getOpportunities());
                        handleClose();
                    }
                }
            });
        } else {
            dispatch(addOpportunity({ opportunity: opp })).then((actionResponse) => {
                if (addOpportunity.fulfilled.match(actionResponse)) {
                    if (
                        actionResponse.payload.responseData &&
                        actionResponse.payload.responseData.status
                    ) {
                        dispatch(getOpportunities());
                        handleClose();
                    }
                }
            });
        }
    };

    /* ── field label component ── */
    const FieldLabel = ({ labelKey, required }) => (
        <Typography
            variant="caption"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.75,
                fontWeight: 700,
                letterSpacing: "0.05em",
                color: "rgba(0,0,0,0.55)",
                fontSize: "0.73rem",
                textTransform: "uppercase",
                transition: TRANSITION_SMOOTH,
                "&::before": {
                    content: '""',
                    display: "inline-block",
                    width: 3,
                    height: 14,
                    borderRadius: 2,
                    background: "linear-gradient(180deg, #FF9015, #FFC100)",
                    flexShrink: 0,
                },
            }}
        >
            <Langs str={labelKey} />
            {required && (
                <Box
                    component="span"
                    sx={{
                        color: "#FF9015",
                        fontWeight: 800,
                        fontSize: "0.8rem",
                        ml: 0.25,
                    }}
                >
                    *
                </Box>
            )}
        </Typography>
    );

    /* ── autocomplete field helper (for large lists) ── */
    const renderAutocomplete = (key, options, optionLabel, optionValue) => {
        const currentValue =
            (options || []).find(
                (o) => o[optionValue] === (selectedItem ? selectedItem[key] : null)
            ) || null;

        return (
            <Autocomplete
                fullWidth
                size="small"
                options={options || []}
                getOptionLabel={(option) => option[optionLabel] || ""}
                value={currentValue}
                onChange={(_e, newVal) =>
                    handleFieldChange(newVal ? newVal[optionValue] : null, key)
                }
                isOptionEqualToValue={(opt, val) =>
                    opt[optionValue] === val[optionValue]
                }
                componentsProps={{
                    popper: { sx: { zIndex: 1600 } },
                }}
                renderInput={(params) => (
                    <TextField {...params} sx={glassFieldSx} />
                )}
            />
        );
    };

    /* ── field definitions ── */
    const visibleFields = [
        { key: "id", label: "ID", fieldType: "disabled" },
        { key: "id_team", label: "TEAM", fieldType: "autocomplete", options: teams, optionLabel: "label", optionValue: "id" },
        { key: "category", label: "category", fieldType: "chipDropdown", options: categoryOptions, chipFn: categoryChipProps },
        { key: "id_poste", label: "POSTE", fieldType: "autocomplete", options: postes, optionLabel: "name", optionValue: "id" },
        { key: "title", label: "title", fieldType: "text", required: true },
        { key: "priority", label: "priority", fieldType: "chipDropdown", options: priorityOptions, chipFn: (v) => ({ color: priorityColor(v), label: v || "—" }) },
        { key: "start_date", label: "start_date", fieldType: "date" },
        { key: "profiles_number", label: "profiles_number", fieldType: "number" },
        { key: "provided_profiles_number", label: "provided_profiles_number", fieldType: "number" },
        { key: "status", label: "status", fieldType: "chipDropdown", options: statusOptions, chipFn: statusChipProps },
        { key: "sub_status", label: "sub_status", fieldType: "chipDropdown", options: subStatusOptions, chipFn: subStatusChipProps },
        { key: "localisation", label: "localisation", fieldType: "string" },
        { key: "description", label: "description", fieldType: "multiline" },
        { key: "competences", label: "competences", fieldType: "multiline" },
        { key: "response_comments", label: "response_comments", fieldType: "multiline" },
        { key: "helped_by", label: "helped_by", fieldType: "autocomplete", options: allCollabs, optionLabel: "label", optionValue: "id" },
        { key: "demanded_by", label: "demanded_by", fieldType: "autocomplete", options: allCollabs, optionLabel: "label", optionValue: "id" },
        { key: "handled_by", label: "handled_by", fieldType: "autocomplete", options: allCollabs, optionLabel: "label", optionValue: "id" },
        { key: "prospect", label: "prospect", fieldType: "multiline" },
    ];

    /* ── render a single field ── */
    const renderField = (field) => {
        const value = selectedItem ? selectedItem[field.key] : "";

        switch (field.fieldType) {
            /* ── disabled string ── */
            case "disabled":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={value || ""}
                        sx={glassFieldSx}
                    />
                );

            /* ── autocomplete (large lists) ── */
            case "autocomplete":
                return renderAutocomplete(
                    field.key,
                    field.options,
                    field.optionLabel,
                    field.optionValue
                );

            /* ── chip dropdown ── */
            case "chipDropdown": {
                return (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        SelectProps={{
                            MenuProps: selectMenuProps,
                            renderValue: (sel) => (
                                <Chip size="small" {...field.chipFn(sel)} />
                            ),
                        }}
                    >
                        {(field.options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                <Chip size="small" {...field.chipFn(opt)} />
                            </MenuItem>
                        ))}
                    </TextField>
                );
            }

            /* ── plain string ── */
            case "string":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                    />
                );

            /* ── multiline text ── */
            case "text":
            case "multiline":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={field.fieldType === "text" ? 2 : 4}
                        maxRows={8}
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                    />
                );

            /* ── date ── */
            case "date":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={toDateStr(value)}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                );

            /* ── number ── */
            case "number":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        value={value ?? ""}
                        onChange={(e) =>
                            handleFieldChange(
                                e.target.value === "" ? null : parseInt(e.target.value, 10),
                                field.key
                            )
                        }
                        sx={glassFieldSx}
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        value={value || ""}
                        disabled
                        sx={glassFieldSx}
                    />
                );
        }
    };

    /* ── header text ── */
    const headerText =
        type === "EDIT" ? (
            <Langs str="OPPORTUNITY_DETAILS" />
        ) : (
            <Langs str="ADD_OPPORTUNITY" />
        );

    /* ════════════════════════════════════════════════════════════════ */
    return (
        <Dialog
            open={!!visible}
            onClose={handleClose}
            fullScreen
            TransitionComponent={SlideUp}
            sx={{
                zIndex: 1400,
                "& .MuiDialog-paper": {
                    background:
                        "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
                },
            }}
        >
            {/* ── sticky app bar (glassmorphic) ── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: "rgba(255,255,255,0.82)",
                    color: "rgba(0,0,0,0.87)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56 }}>
                    <IconButton
                        edge="start"
                        onClick={handleClose}
                        sx={{
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            color: "rgba(0,0,0,0.7)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                                backgroundColor: "rgba(255,144,21,0.08)",
                                color: "#FF9015",
                            },
                            "&:active": { transform: "scale(0.94)" },
                        }}
                        aria-label="Fermer"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            letterSpacing: "-0.01em",
                            fontSize: "1.05rem",
                        }}
                    >
                        {headerText}
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ── scrollable form body ── */}
            <Box
                sx={{
                    mt: 0,
                    px: 2,
                    pt: 2.5,
                    pb: showSave ? "110px" : 3,
                    overflowY: "auto",
                    flex: 1,
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255,144,21,0.2)",
                        borderRadius: 3,
                    },
                }}
            >
                {visibleFields.map((field, index) => (
                    <Fade in key={field.key} timeout={200 + index * 40}>
                        <Box sx={{ mb: 0.5 }}>
                            <FieldLabel labelKey={field.label} required={field.required} />
                            {renderField(field)}
                        </Box>
                    </Fade>
                ))}
            </Box>

            {/* ── sticky bottom save button ── */}
            <Fade in={showSave}>
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 2,
                        bgcolor: "rgba(255,255,255,0.88)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "0 -4px 20px rgba(0,0,0,0.04), 0 -1px 6px rgba(0,0,0,0.03)",
                        zIndex: 10,
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.2), transparent)",
                        },
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={type === "EDIT" ? <SaveIcon /> : <AddIcon />}
                        onClick={handleSave}
                        sx={{
                            borderRadius: "999px",
                            minHeight: 48,
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: "1rem",
                            letterSpacing: "-0.01em",
                            background:
                                "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                                background:
                                    "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)",
                                boxShadow:
                                    "0 6px 24px rgba(255,144,21,0.45), 0 2px 6px rgba(255,144,21,0.25)",
                                transform: "translateY(-1px)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                            boxShadow:
                                "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                        }}
                    >
                        {type === "EDIT" ? <Langs str="SAVE" /> : <Langs str="ADD" />}
                    </Button>
                </Box>
            </Fade>
        </Dialog>
    );
}
